import React, { useRef, useEffect, useState } from 'react';
import * as Sentry from "@sentry/react";
import { MessageSquare, Send, X, AlertCircle, Camera, Check, Trash2, Pencil, MousePointer2, Circle, Square, Type } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDanceStore } from '../store/useDanceStore';
import { DanceState } from '../types';
import html2canvas from 'html2canvas';
import { trackEvent } from '../lib/analytics';
import { executeRecaptcha } from '../utils/recaptcha';
import { API_BASE_URL } from '../lib/api';
import { shouldInitializeSentry } from '../instrument';


export const FeedbackDialog: React.FC = () => {
  const isOpen = useDanceStore((state: DanceState) => state.isFeedbackOpen);
  const setIsOpen = useDanceStore((state: DanceState) => state.setIsFeedbackOpen);
  const message = useDanceStore((state: DanceState) => state.feedbackMessage);
  const setMessage = useDanceStore((state: DanceState) => state.setFeedbackMessage);
  const screenshot = useDanceStore((state: DanceState) => state.feedbackScreenshot);
  const setScreenshot = useDanceStore((state: DanceState) => state.setFeedbackScreenshot);
  const { t } = useTranslation();
  
  const [isSending, setIsSending] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [isCapturing, setIsCapturing] = React.useState(false);
  const [isAnnotating, setIsAnnotating] = React.useState(false);
  const [drawingMode, setDrawingMode] = useState<'pen' | 'circle' | 'rect' | 'text' | 'none'>('rect');
  const [textInput, setTextInput] = useState<{ x: number, y: number, canvasX: number, canvasY: number, value: string } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const startPos = useRef<{ x: number, y: number } | null>(null);
  const snapshot = useRef<ImageData | null>(null);

  const prevIsOpen = useRef(isOpen);
  const sentRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      trackEvent('feedback_opened');
      sentRef.current = false;
    } else if (prevIsOpen.current && !isOpen) {
      if (!sentRef.current) {
        trackEvent('feedback_closed');
      }
    }
    prevIsOpen.current = isOpen;
  }, [isOpen]);


  useEffect(() => {
    if (isOpen && textareaRef.current && !isAnnotating) {
      textareaRef.current.focus();
    }
    if (!isOpen) {
      setShowSuccess(false);
      setIsSending(false);
      setIsAnnotating(false);
      setErrorMsg(null);
    }

  }, [isOpen, isAnnotating]);

  // Handle drawing on the canvas
  useEffect(() => {
    if (isAnnotating && canvasRef.current && screenshot) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
      };
      img.src = screenshot;
    }
  }, [isAnnotating, screenshot]);

  const commitText = () => {
    // Note: We use a ref-like approach or pass the value to ensure we don't have race conditions with state
    if (!textInput || !textInput.value.trim() || !canvasRef.current) {
      setTextInput(null);
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      ctx.font = `600 ${18 * scaleX}px Inter, sans-serif`;
      ctx.fillStyle = '#ef4444';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(textInput.value, textInput.canvasX, textInput.canvasY);
    }
    setTextInput(null);
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (drawingMode === 'none' || drawingMode === 'text') return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    isDrawing.current = true;
    startPos.current = { x, y };
    
    // Save snapshot to restore for shape previews
    snapshot.current = ctx.getImageData(0, 0, canvas.width, canvas.height);

    if (drawingMode === 'pen') {
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };

  const handleMouseUp = (e: React.MouseEvent | React.TouchEvent) => {
    if (drawingMode === 'text') {
      // If we're already typing, commit it first
      if (textInput && textInput.value.trim()) {
        commitText();
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      let clientX, clientY;
      if ('touches' in e) {
        clientX = e.changedTouches[0].clientX;
        clientY = e.changedTouches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;

      setTextInput({ 
        x: clientX - rect.left, 
        y: clientY - rect.top, 
        canvasX: x, 
        canvasY: y, 
        value: '' 
      });
      return;
    }

    stopDrawing();
  };

  const stopDrawing = () => {
    isDrawing.current = false;
    startPos.current = null;
    snapshot.current = null;
    const ctx = canvasRef.current?.getContext('2d');
    ctx?.beginPath();
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing.current || !canvasRef.current || drawingMode === 'none') return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx || !startPos.current) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if ('touches' in e) {
      clientX = (e as React.TouchEvent).touches[0].clientX;
      clientY = (e as React.TouchEvent).touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    ctx.lineWidth = 4 * scaleX;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#ef4444'; // Red for visibility

    if (drawingMode === 'pen') {
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else {
      // Shape drawing (Rect or Circle)
      if (snapshot.current) {
        ctx.putImageData(snapshot.current, 0, 0);
      }

      const dx = x - startPos.current.x;
      const dy = y - startPos.current.y;

      if (drawingMode === 'rect') {
        ctx.strokeRect(startPos.current.x, startPos.current.y, dx, dy);
      } else if (drawingMode === 'circle') {
        // Diameter-based circle: start point and end point are opposite sides
        const centerX = (startPos.current.x + x) / 2;
        const centerY = (startPos.current.y + y) / 2;
        const radius = Math.sqrt(dx * dx + dy * dy) / 2;
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  };

  const handleCapture = async () => {
    setIsCapturing(true);
    setErrorMsg(null);
    document.body.classList.add('is-capturing');
    // Optimizing capture: scale 1, jpeg format to avoid 413 Payload Too Large
    setTimeout(async () => {
      try {
        // Pre-snapshot any live WebGL canvases (e.g. the Three.js stage) from
        // their real, already-rendered contents. html2canvas clones the whole
        // DOM into an offscreen iframe to render it; cloning a WebGL canvas
        // there allocates another GL context, which can push the browser past
        // its concurrent-context limit and evict the original ("Context Lost"),
        // leaving a 0x0 canvas that crashes html2canvas's createPattern call.
        // Swapping in a plain <img> of a pre-captured snapshot avoids that.
        const liveCanvases = Array.from(document.querySelectorAll('canvas')) as HTMLCanvasElement[];
        const canvasSnapshots = new Map<HTMLCanvasElement, string>();
        liveCanvases.forEach((c) => {
          if (c.width > 0 && c.height > 0) {
            try {
              canvasSnapshots.set(c, c.toDataURL('image/png'));
            } catch {
              // Cross-origin or otherwise tainted canvas; leave it to html2canvas.
            }
          }
        });

        const canvas = await html2canvas(document.body, {
          backgroundColor: '#020617',
          logging: false,
          useCORS: true,
          scale: 1, // Reduced from 2 to save bandwidth
          ignoreElements: (el) => el.classList.contains('dialog-overlay') || el.tagName === 'IFRAME',
          onclone: (clonedDoc) => {
            liveCanvases.forEach((liveCanvas, index) => {
              const dataUrl = canvasSnapshots.get(liveCanvas);
              if (!dataUrl) return;
              const clonedCanvas = clonedDoc.querySelectorAll('canvas')[index] as HTMLCanvasElement | undefined;
              if (!clonedCanvas || !clonedCanvas.parentNode) return;
              const img = clonedDoc.createElement('img');
              img.src = dataUrl;
              img.style.cssText = clonedCanvas.style.cssText;
              img.width = clonedCanvas.width;
              img.height = clonedCanvas.height;
              img.className = clonedCanvas.className;
              clonedCanvas.parentNode.replaceChild(img, clonedCanvas);
            });
          }
        });

        const dataUrl = canvas.toDataURL('image/jpeg', 0.8); // Switched to JPEG 0.8 quality
        setScreenshot(dataUrl);
        trackEvent('feedback_screenshot_captured');
        setIsAnnotating(true);
      } catch (error) {
        console.error('Failed to capture screenshot:', error);
        Sentry.captureException(error);
        setErrorMsg(t('feedbackDialog.screenshot_capture_failed'));
        trackEvent('feedback_screenshot_failed');
      } finally {
        setIsCapturing(false);
        document.body.classList.remove('is-capturing');
      }
    }, 100);
  };

  const saveAnnotation = () => {
    if (canvasRef.current) {
      setScreenshot(canvasRef.current.toDataURL('image/png'));
      trackEvent('feedback_screenshot_annotated');
    }
    setIsAnnotating(false);
  };

  const handleSubmit = async () => {
    if (!message.trim() || isSending) return;

    setIsSending(true);
    setErrorMsg(null);
    try {
      if (!shouldInitializeSentry) {
        throw new Error(t('feedbackDialog.reporting_unavailable'));
      }

      // 1. Verify captcha
      console.log('Generating recaptcha token for feedback...');
      const recaptchaToken = await executeRecaptcha('feedback');
      if (recaptchaToken) {
        console.log('Verifying recaptcha token on backend...');
        const verifyResponse = await fetch(`${API_BASE_URL}/api/verify-captcha`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-recaptcha-token': recaptchaToken,
          },
        });
        if (!verifyResponse.ok) {
          const errData = await verifyResponse.json().catch(() => ({}));
          throw new Error(errData.error || 'reCAPTCHA verification failed.');
        }
      }

      // 2. Send feedback to Sentry
      const hints: any = {};
      if (screenshot) {
        // Convert to Uint8Array for Sentry
        const res = await fetch(screenshot);
        const blob = await res.blob();
        const arrayBuffer = await blob.arrayBuffer();
        hints.attachments = [{
          filename: 'screenshot.jpg',
          data: new Uint8Array(arrayBuffer),
          contentType: 'image/jpeg'
        }];
      }

      console.log('Sending custom feedback to Sentry...', { message: message.trim(), hasAttachment: !!screenshot });
      Sentry.captureFeedback({
        message: message.trim(),
      }, hints);
      
      trackEvent('feedback_sent', { has_screenshot: !!screenshot });
      sentRef.current = true;
      setShowSuccess(true);

      // Removed auto-close timeout to allow user to manually close
      setMessage('');
      setScreenshot(null);
    } catch (error: any) {
      console.error('Failed to send feedback:', error);
      setErrorMsg(error?.message || 'Failed to submit feedback.');
      setIsSending(false);
    }
  };

  if (!isOpen || isCapturing) return null;

  // Annotated Screenshot View
  if (isAnnotating) {
    return (
      <div className="feedback-dialog-overlay-annotate" data-sentry-block>
        <div className="feedback-dialog-header-annotate">
          <div>
            <h2 className="feedback-dialog-title-annotate">{t('feedbackDialog.annotate_screenshot')}</h2>
            <p className="feedback-dialog-subtitle-annotate">{t('feedbackDialog.highlight_issues')}</p>
          </div>
          <div className="feedback-dialog-controls-annotate">
             <div className="feedback-dialog-tool-group-annotate">
                 <button 
                   onClick={() => {
                     setDrawingMode('rect');
                     trackEvent('feedback_annotation_tool_selected', { tool: 'rect' });
                   }}
                   className={`feedback-dialog-tool-btn ${drawingMode === 'rect' ? 'active' : ''}`}
                   title={t('feedbackDialog.rectangle')}
                 >
                   <Square size={20} />
                 </button>
                 <button 
                   onClick={() => {
                     setDrawingMode('circle');
                     trackEvent('feedback_annotation_tool_selected', { tool: 'circle' });
                   }}
                   className={`feedback-dialog-tool-btn ${drawingMode === 'circle' ? 'active' : ''}`}
                   title={t('feedbackDialog.circle')}
                 >
                   <Circle size={20} />
                 </button>
                 <button 
                   onClick={() => {
                     setDrawingMode('text');
                     trackEvent('feedback_annotation_tool_selected', { tool: 'text' });
                   }}
                   className={`feedback-dialog-tool-btn ${drawingMode === 'text' ? 'active' : ''}`}
                   title={t('feedbackDialog.text')}
                 >
                   <Type size={20} />
                 </button>
                 <button 
                   onClick={() => {
                     setDrawingMode('pen');
                     trackEvent('feedback_annotation_tool_selected', { tool: 'pen' });
                   }}
                   className={`feedback-dialog-tool-btn ${drawingMode === 'pen' ? 'active' : ''}`}
                   title={t('feedbackDialog.pen')}
                 >
                   <Pencil size={20} />
                 </button>
             </div>
             <button onClick={saveAnnotation} className="save-btn feedback-dialog-btn-done">
               <Check size={18} /> {t('topMenu.done')}
             </button>
             <button 
               onClick={() => { setIsAnnotating(false); setScreenshot(null); }}
               className="feedback-dialog-btn-close-annotate"
               title={t('feedbackDialog.cancel_clear')}
             >
               <X size={20} />
             </button>
          </div>
        </div>
        
        <div className="feedback-dialog-canvas-wrapper">
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={handleMouseUp}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={handleMouseUp}
            className="feedback-dialog-canvas"
            style={{ 
              cursor: drawingMode !== 'none' ? 'crosshair' : 'grab'
            }}
          />
          {textInput && (
            <input
              autoFocus
              value={textInput.value}
              onChange={(e) => setTextInput({ ...textInput, value: e.target.value })}
              onBlur={commitText}
              onKeyDown={(e) => e.key === 'Enter' && commitText()}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className="feedback-dialog-annotation-input"
              style={{
                left: `${textInput.x}px`,
                top: `${textInput.y}px`
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="dialog-overlay feedback-dialog-overlay"
      data-sentry-block
      onClick={() => !isSending && setIsOpen(false)}
    >
      <div 
        className="dialog-content glass-panel feedback-dialog-content"
        onClick={e => e.stopPropagation()}
      >
        {!showSuccess && (
          <div className="feedback-dialog-header">
            <div>
              <div className="feedback-dialog-title-row">
                <div className="feedback-dialog-icon-wrapper">
                  <MessageSquare size={20} />
                </div>
                <h2 className="feedback-dialog-title">{t('feedbackDialog.feedback_report')}</h2>
              </div>
              <p className="feedback-dialog-subtitle">
                {t('feedbackDialog.help_us_improve')}
              </p>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="feedback-dialog-close-btn"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {showSuccess ? (
          <div className="feedback-dialog-success-view">
            <div className="feedback-dialog-success-icon-wrapper">
              <Send size={32} />
            </div>
            <h3 className="feedback-dialog-success-title">{t('feedbackDialog.sent')}</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="feedback-dialog-success-close-btn"
            >
              {t('common.close')}
            </button>
          </div>
        ) : (
          <>
            {errorMsg && (
              <div className="auth-modal-error" style={{ marginBottom: '12px' }}>
                {errorMsg}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={t('feedbackDialog.placeholder')}
              className="feedback-dialog-textarea"
            />

            <div className="feedback-dialog-screenshot-wrapper">
              {screenshot ? (
                <div className="feedback-dialog-screenshot-container">
                  <img src={screenshot} alt="Visual context" className="feedback-dialog-screenshot-img" />
                  <div className="feedback-dialog-screenshot-overlay">
                    <div className="feedback-dialog-screenshot-actions">
                      <button 
                        onClick={() => setIsAnnotating(true)}
                        className="feedback-dialog-screenshot-edit-btn"
                      >
                        <Pencil size={12} /> {t('feedbackDialog.edit')}
                      </button>
                      <button 
                        onClick={() => {
                          setScreenshot(null);
                          trackEvent('feedback_screenshot_deleted');
                        }}
                        className="feedback-dialog-screenshot-delete-btn"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleCapture}
                  className="feedback-dialog-add-screenshot-btn"
                >
                  <Camera size={24} />
                  <span>{t('feedbackDialog.include_screenshot')}</span>
                </button>
              )}
            </div>

            <div className="feedback-dialog-footer">
              <button 
                onClick={() => setIsOpen(false)}
                className="feedback-dialog-btn-cancel"
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={handleSubmit}
                disabled={!message.trim() || isSending}
                className={`feedback-dialog-btn-submit ${message.trim() ? 'active' : ''}`}
              >
                {isSending ? t('crashFallback.sending') : <><Send size={18} /> {t('feedbackDialog.send')}</>}
              </button>
            </div>
          </>
        )}


      </div>
    </div>
  );
};
